import { AuthenticationApplication } from 'api';
import { Environment } from 'types';
import {
  DEFAULT_SSO_COOKIE_DOMAIN,
  DEFAULT_SSO_LOCALHOST_COOKIE_DOMAIN,
} from './constants';
import { GetSpareminToken, SpareminAuthConfiguration } from './types';

let appleRedirectURI: string | undefined;
let application: AuthenticationApplication | undefined;
let getSpareminTokenFn: undefined | GetSpareminToken;
let environment: Environment;
let ssoDomain: string | undefined;
let configured = false;

export function getAppleRedirectURI() {
  if (!appleRedirectURI) {
    throw new Error(
      'appleRedirectURI not set.  Run configure() before using @sparemin/auth',
    );
  }

  return appleRedirectURI;
}

export function getEnvironment(): Environment {
  if (!environment || !configured) {
    throw new Error(
      'No environment set.  Run configure() before using @sparemin/auth',
    );
  }

  return environment;
}

export function getSSODomain(): string {
  if (!ssoDomain) {
    throw new Error(
      'No SSO domain found.  Run configure() before using @sparemin/auth',
    );
  }

  return ssoDomain;
}

// function avoids exporting mutable getSpareminTokenFn
export function getSpareminToken() {
  return getSpareminTokenFn?.();
}

export function getApplication(): AuthenticationApplication {
  if (!application) {
    throw new Error(
      'No application found.  Run configure() before using @sparemin/auth',
    );
  }
  return application;
}

/**
 * Configures the library.  This function must be called before using any
 * funtionality.  If `configure` is called more than once, an error will be
 * thrown.
 *
 * A full configuration is not necessary for using only parts of the application.
 * Errors will be thrown when trying to access a value that has not been provided
 * to the `configure` function.  As long as the app runs with no errors, the full
 * configuration object is not required.
 */
export default function configure(config: SpareminAuthConfiguration) {
  if (process.env.NODE_ENV !== 'test' && configured) {
    throw new Error(
      '@sparemin/auth configured multiple times, which can cause errors ' +
        'if the library is configured with different values. Make sure to only call configure() once.',
    );
  }

  appleRedirectURI = config.appleRedirectURI;
  application = config.application;
  environment = config.environment;
  getSpareminTokenFn = config.getSpareminToken;

  ssoDomain =
    environment === 'local'
      ? DEFAULT_SSO_LOCALHOST_COOKIE_DOMAIN
      : config?.sso?.domain ?? DEFAULT_SSO_COOKIE_DOMAIN;

  configured = true;
}
